import { useState, useEffect, useRef } from "react";
import renderMermaidToPng from "Utils/mermaidToPng";
import { parseMermaidToText } from "Components/SbMarkdownEditor/useMermaid";

/** Regex that matches fenced mermaid code blocks in markdown. */
const MERMAID_FENCE_RE = /```mermaid\s*\n([\s\S]*?)```/g;

/**
 * Splits markdown into an ordered array of segments, preserving the
 * exact position of every mermaid block relative to surrounding text.
 *
 * Returns:
 *   [
 *     { type: "markdown", content: "...text before first diagram..." },
 *     { type: "mermaid",  code: "graph TD\n  A-->B" },
 *     { type: "markdown", content: "...text between diagrams..." },
 *     ...
 *   ]
 *
 * @param {string} markdown  Raw markdown string.
 * @returns {Array<{type: string, content?: string, code?: string}>}
 */
const splitMarkdownSegments = (markdown) => {
  if (!markdown) return [{ type: "markdown", content: "" }];

  const segments = [];
  const re = new RegExp(MERMAID_FENCE_RE.source, MERMAID_FENCE_RE.flags);
  let lastIndex = 0;
  let match;

  while ((match = re.exec(markdown)) !== null) {
    // Text before this mermaid block.
    const textBefore = markdown.slice(lastIndex, match.index);
    if (textBefore) {
      segments.push({ type: "markdown", content: textBefore });
    }

    // The mermaid block itself (captured group = code without fences).
    segments.push({ type: "mermaid", code: match[1] });

    lastIndex = re.lastIndex;
  }

  // Remaining text after the last mermaid block.
  const textAfter = markdown.slice(lastIndex);
  if (textAfter) {
    segments.push({ type: "markdown", content: textAfter });
  }

  return segments.length > 0 ? segments : [{ type: "markdown", content: markdown }];
};

/**
 * Hook that pre-renders every mermaid diagram in markdown to PNG data-URLs
 * while preserving the exact document order of content segments.
 *
 * For each diagram that fails to render as PNG, it falls back to the
 * structured text representation via `parseMermaidToText`.
 *
 * @param {string} markdownContent  Raw markdown that may contain mermaid blocks.
 * @returns {{
 *   segments: Array<{
 *     type: "markdown" | "mermaid",
 *     content?: string,
 *     code?: string,
 *     dataUrl?: string|null,
 *     fallback?: { diagramType: string, relationships: string[] }|null
 *   }>,
 *   isReady: boolean
 * }}
 */
const useMermaidPdfImages = (markdownContent) => {
  const [segments, setSegments] = useState(() => splitMarkdownSegments(markdownContent));
  const [isReady, setIsReady] = useState(true);
  const prevContentRef = useRef("");

  useEffect(() => {
    const rawSegments = splitMarkdownSegments(markdownContent);
    const hasMermaid = rawSegments.some((s) => s.type === "mermaid");

    // No mermaid blocks — return segments as-is, immediately ready.
    if (!hasMermaid) {
      setSegments(rawSegments);
      setIsReady(true);
      return;
    }

    // Skip re-render if the markdown hasn't changed.
    if (prevContentRef.current === markdownContent) {
      return;
    }
    prevContentRef.current = markdownContent;

    let cancelled = false;
    setIsReady(false);

    const renderAll = async () => {
      const resolved = await Promise.all(
        rawSegments.map(async (segment) => {
          if (segment.type !== "mermaid") return segment;

          const result = await renderMermaidToPng(segment.code);
          const fallback = result ? null : parseMermaidToText(segment.code);

          return {
            ...segment,
            dataUrl: result?.dataUrl || null,
            width: result?.width || 0,
            height: result?.height || 0,
            fallback,
          };
        }),
      );

      if (!cancelled) {
        setSegments(resolved);
        setIsReady(true);
      }
    };

    renderAll();

    return () => {
      cancelled = true;
    };
  }, [markdownContent]);

  return { segments, isReady };
};

export default useMermaidPdfImages;
